import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { paymentStatusService } from '../services/PaymentStatusService';
import { clientService } from '../services/clientService';
import { paymentSessionService } from '../services/paymentSessionService';
import { trackEvent } from '../lib/analytics';

const Thanks = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    const syncPurchase = async () => {
      if (!token) {
        console.warn('No token provided on thank you page');
        navigate('/');
        return;
      }

      try {
        const finalizeEndpoints = ['/api/checkout/finalize', '/checkoutFinalize.php'];
        let finalized = false;
        let finalizeError = 'Impossible de finaliser le checkout';

        for (const endpoint of finalizeEndpoints) {
          try {
            const response = await fetch(endpoint, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ token }),
            });

            const payload = await response.json().catch(() => null);
            if (!response.ok) {
              finalizeError = payload?.error || finalizeError;
              continue;
            }

            finalized = true;
            break;
          } catch {
            // Try next endpoint candidate.
          }
        }

        if (!finalized) {
          throw new Error(finalizeError);
        }
        trackEvent('purchase_finalize_success', {
          token,
        });
      } catch (error) {
        trackEvent('purchase_finalize_error', {
          token,
          reason: error instanceof Error ? error.message : 'unknown_error',
        });
        console.error('Error finalizing checkout on server:', error);
      }

      try {
        await clientService.captureClientFromPayment(token);
      } catch (error) {
        console.error('Error creating client from payment:', error);
      }

      paymentSessionService.clearSession(token);

      // Persist "paid" status locally to keep flow coherent even without PHP backend.
      paymentStatusService.updateStatus(token, 'paid').catch((error) => {
        console.error('Error setting local payment status:', error);
      });
    };

    syncPurchase().catch((error) => {
      console.error('Error finalizing paid order:', error);
    });
  }, [navigate, token]);

  return (
    <>
      <Helmet>
        <title>Merci pour votre commande - Couteau Suisse Digital</title>
        <meta name="description" content="Merci pour votre confiance. Votre commande a été confirmée et sera traitée dans les plus brefs délais." />
      </Helmet>

      <main className="min-h-screen py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-8 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="w-24 h-24 bg-green-500 rounded-full mx-auto mb-8 flex items-center justify-center"
            >
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="text-3xl font-bold mb-4"
            >
              Merci pour votre commande !
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-xl text-gray-600 mb-8"
            >
              Votre commande a été confirmée et sera traitée dans les plus brefs délais.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="bg-gray-50 rounded-xl p-6 mb-8"
            >
              <h2 className="text-xl font-semibold mb-4">Prochaines étapes</h2>
              <ul className="space-y-4 text-left">
                <li className="flex items-start gap-3">
                  <span className="text-neon-orange">1.</span>
                  <span>Vous allez recevoir un email de confirmation avec les détails de votre commande</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-neon-orange">2.</span>
                  <span>Notre équipe vous contactera sous 24h pour planifier un appel de cadrage</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-neon-orange">3.</span>
                  <span>Nous établirons ensemble un planning détaillé pour votre projet</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link
                to="/"
                className="bg-neon-orange text-white px-8 py-4 rounded-full font-semibold hover:bg-[#FF4500] transition-colors duration-300"
              >
                Retour à l'accueil
              </Link>
              <Link
                to="/contact"
                className="border-2 border-neon-orange text-neon-orange px-8 py-4 rounded-full font-semibold hover:bg-neon-orange hover:text-white transition-all duration-300"
              >
                Nous contacter
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </main>
    </>
  );
};

export default Thanks;
